import { useQuery } from '@tanstack/react-query';
import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { useAuth } from '@/contexts/AuthContext';

function isAllocationManagerToday(allocationManagerUntil?: string | null): boolean {
    if (!allocationManagerUntil) return false;
    const today = new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
    return allocationManagerUntil >= today;
}

async function fetchIsVacationManager(email?: string | null): Promise<boolean> {
    if (!email) return false;

    const results = await Promise.allSettled([
        getDocs(
            query(
                collection(db, 'collaborators_public'),
                where('responsibleEmail', '==', email),
                limit(1)
            )
        ),
        getDocs(
            query(
                collection(db, 'vacationRequests'),
                where('managerEmail', '==', email),
                limit(1)
            )
        ),
        getDocs(
            query(
                collection(db, 'dpRequests'),
                where('type', '==', 'day_off'),
                where('managerEmail', '==', email),
                limit(1)
            )
        )
    ]);

    return results.some(result => result.status === 'fulfilled' && !result.value.empty);
}

interface UseIsVacationManagerResult {
    isManager: boolean;
    isLoading: boolean;
}

export function useIsVacationManager(): UseIsVacationManagerResult {
    const { user } = useAuth();

    const isAllocationManager = isAllocationManagerToday(user?.allocationManagerUntil);

    const { data, isLoading } = useQuery<boolean>({
        queryKey: ['is_vacation_manager', user?.email],
        queryFn: () => {
            if (isAllocationManager) return Promise.resolve(true);
            return fetchIsVacationManager(user?.email);
        },
        enabled: Boolean(user?.email),
        staleTime: 10 * 60 * 1000,
        retry: 1,
    });

    return {
        isManager: isAllocationManager || (data ?? false),
        isLoading: isAllocationManager ? false : isLoading,
    };
}
